import * as tus from 'tus-js-client'
import {sha256} from 'js-sha256'
import {API_URL} from '../../../../tenant/apiBase'
import {tenantContextHeaders} from '../../../auth/core/AuthHelpers'
// tus creation endpoint on the Lumen API (behind the same supplierAuth as the
// rest of the partner portal). The completed upload is then referenced by
// `images_upload_key` on product/bulk (add) or product/upload (zip-only).
export const TUS_IMAGES_ENDPOINT = `${API_URL}/api/partner-portal/product/upload/tus`

// 20 MB — comfortably under Cloudflare's 100 MB per-request cap (leaving room
// for multipart/headers) while keeping retries cheap on flaky mobile networks.
export const TUS_CHUNK_SIZE = 20 * 1024 * 1024

// Streamed retry backoff — resumes are cheap because only the failed chunk
// (not the whole file) is re-sent.
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000]

export type TusProgress = {
  bytesUploaded: number
  bytesTotal: number
  percent: number
}

export type UploadZipViaTusOptions = {
  onProgress?: (p: TusProgress) => void
  /** Fired when a chunk failed and tus is about to retry — drives a "reconnecting" UI. */
  onRetry?: (attempt: number) => void
  /** Progress of the SHA-256 pass over the file, before the first byte is sent. */
  onHashProgress?: (percent: number) => void
  /** Aborting it stops the upload ("Stop upload") and deletes what the server already has. */
  signal?: AbortSignal
}

/** The rejection when the upload is stopped through `signal`. */
function abortError(): Error {
  const err = new Error('Upload was stopped.')
  err.name = 'AbortError'
  return err
}

/**
 * Largest ZIP sent the old way, in one request, when the API cannot take it in pieces: under
 * Cloudflare's 100 MB request cap with room for the sheet sent alongside.
 */
export const SINGLE_REQUEST_MAX_BYTES = 90 * 1024 * 1024

/** The HTTP status that failed the upload, 0 when there was no answer (connection lost). */
export function uploadFailureStatus(err: unknown): number {
  const res = (err as tus.DetailedError | undefined)?.originalResponse
  return res ? res.getStatus() : 0
}

/**
 * The request that creates the upload. With overridePatchMethod every piece is a POST too, carrying
 * X-HTTP-Method-Override: PATCH, so the method alone does not tell them apart.
 */
export function isCreationRequest(req: tus.HttpRequest): boolean {
  return req.getMethod() === 'POST' && !req.getHeader('X-HTTP-Method-Override')
}

/**
 * True when the upload failed on the request that creates it, with no answer or a 404/405: what an
 * API without the chunked upload looks like (its CORS answer lacks the tus headers, or the route is
 * missing). hisbah-ui goes live on merge, possibly before the API does, so the caller then sends a
 * ZIP under SINGLE_REQUEST_MAX_BYTES the old way instead of failing every images upload.
 */
export function chunkedUploadUnavailable(err: unknown): boolean {
  const req = (err as tus.DetailedError | undefined)?.originalRequest
  if (!req || !isCreationRequest(req)) {
    return false
  }
  const status = uploadFailureStatus(err)
  return status === 0 || status === 404 || status === 405
}

/** The server's own message for a failed upload request, if it sent one ({message}). */
export function uploadFailureMessage(err: unknown): string {
  try {
    const body = (err as tus.DetailedError | undefined)?.originalResponse?.getBody()
    return body ? String(JSON.parse(body).message || '') : ''
  } catch {
    return ''
  }
}

/**
 * Compute the SHA-256 of a File without loading it fully into memory: hash it in
 * 20 MB slices via js-sha256's incremental API (the browser's WebCrypto digest
 * can't stream a multi-hundred-MB file). Returns the lowercase hex digest the
 * server verifies the reassembled upload against.
 */
export async function sha256OfFile(
  file: File,
  signal?: AbortSignal,
  onProgress?: (percent: number) => void
): Promise<string> {
  const hasher = sha256.create()
  const sliceSize = TUS_CHUNK_SIZE
  for (let start = 0; start < file.size; start += sliceSize) {
    if (signal?.aborted) {
      throw abortError()
    }
    const end = Math.min(start + sliceSize, file.size)
    const buf = await file.slice(start, end).arrayBuffer()
    hasher.update(buf)
    onProgress?.(Math.round((end / file.size) * 100))
  }
  return hasher.hex()
}

/** Base64 of a string's UTF-8 bytes, as tus Upload-Metadata values are encoded. */
function base64Utf8(value: string): string {
  let binary = ''
  new TextEncoder().encode(value).forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

/**
 * Upload an images ZIP with the tus resumable protocol and resolve with the
 * server upload key (the id at the tail of the tus upload URL). The caller sends
 * that key as `images_upload_key` to the existing product/bulk or product/upload
 * endpoint, so a >100 MB ZIP crosses Cloudflare in <100 MB chunks while the
 * downstream processing stays byte-identical to a small upload.
 *
 * tus-js-client runs its own XHR stack (it does NOT go through axios), so the
 * auth token is attached here explicitly — the axios Bearer interceptor never
 * runs for these requests, and the server reads the `token` header.
 */
export async function uploadZipViaTus(
  file: File,
  token: string | undefined,
  options: UploadZipViaTusOptions = {}
): Promise<string> {
  const metadata: Record<string, string> = {
    filename: file.name,
    filetype: file.type || 'application/zip',
  }
  // The SHA-256 only travels on the creation request, so it is computed there, once: resuming
  // an upload the server already has (after a reload or a failed attempt) does not re-read the file.
  let sha: Promise<string> | null = null

  return new Promise<string>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: TUS_IMAGES_ENDPOINT,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: RETRY_DELAYS,
      // Some browser/proxy paths accept creation + HEAD but drop a PATCH body
      // after its successful preflight. Tus' standard method override keeps the
      // same protocol semantics while carrying chunks over POST.
      overridePatchMethod: true,
      // Resume across page reloads / dropped sessions: tus stores the upload URL
      // (keyed by a fingerprint of the file) in localStorage and a fresh Upload
      // for the same file picks up where it left off (see the resume probe below).
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      // No `metadata` option: Upload-Metadata (with the SHA-256) is set once, below. Given both,
      // the browser sends the two values joined in one header.
      onBeforeRequest: async (req) => {
        // The tenant-context token lives about five minutes and a big ZIP takes longer to send:
        // every piece carries the current one (tus-js-client does not go through axios, whose
        // interceptor stamps it everywhere else).
        Object.entries(tenantContextHeaders()).forEach(([name, value]) =>
          req.setHeader(name, value)
        )
        if (!isCreationRequest(req)) {
          return
        }
        sha = sha || sha256OfFile(file, options.signal, options.onHashProgress)
        const values: Record<string, string> = {...metadata, sha256: await sha}
        req.setHeader(
          'Upload-Metadata',
          Object.keys(values)
            .map((key) => `${key} ${base64Utf8(values[key])}`)
            .join(',')
        )
      },
      headers: {
        // Mirror the app's dual auth convention: `token` is what the Lumen
        // supplierAuth middleware reads; Authorization is sent too for parity
        // with the axios interceptor the rest of the app relies on.
        token: token || '',
        Authorization: token ? `Bearer ${token}` : '',
      },
      onShouldRetry: (err, attempt) => {
        // Don't retry permanent client errors — a 4xx (bad request, 404 gone,
        // 410 expired, 415 wrong type, 460 checksum-mismatch-and-deleted) won't
        // succeed on replay and would just burn attempts against a dead upload.
        // Retry only network drops and transient server errors, plus 409 Conflict
        // (an offset mismatch, fixed by the HEAD a retry starts with) and 423
        // Locked (a concurrent PATCH). This mirrors tus-js-client's own default
        // policy, which also stops once every entry of retryDelays has been used.
        if (options.signal?.aborted) {
          return false
        }
        const status =
          err && (err as tus.DetailedError).originalResponse
            ? (err as tus.DetailedError).originalResponse!.getStatus()
            : 0
        if (status >= 400 && status < 500 && status !== 409 && status !== 423) {
          return false
        }
        options.onRetry?.(attempt + 1)
        return true
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percent = bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0
        options.onProgress?.({bytesUploaded, bytesTotal, percent})
      },
      onSuccess: () => {
        const key = uploadKeyFromUrl(upload.url)
        if (!key) {
          reject(new Error('tus upload completed without a resolvable upload URL'))
          return
        }
        resolve(key)
      },
      onError: (error) => {
        reject(error)
      },
    })

    const stop = () => {
      // true: also delete the pieces the server already has.
      upload.abort(true).catch(() => {})
      reject(abortError())
    }
    if (options.signal?.aborted) {
      stop()
      return
    }
    options.signal?.addEventListener('abort', stop, {once: true})

    // Resume a prior interrupted upload of the same file if one exists, else
    // start fresh. This is what makes a mid-upload network drop resumable.
    upload
      .findPreviousUploads()
      .then((previous) => {
        if (previous.length > 0) {
          upload.resumeFromPreviousUpload(previous[0])
        }
        if (!options.signal?.aborted) {
          upload.start()
        }
      })
      .catch(() => {
        if (!options.signal?.aborted) {
          upload.start()
        }
      })
  })
}

/** Extract the upload id (last path segment) from a tus upload URL. */
function uploadKeyFromUrl(url: string | null): string | null {
  if (!url) {
    return null
  }
  try {
    const path = new URL(url, API_URL).pathname.replace(/\/+$/, '')
    const id = path.substring(path.lastIndexOf('/') + 1)
    return id || null
  } catch {
    const trimmed = url.replace(/\/+$/, '')
    const id = trimmed.substring(trimmed.lastIndexOf('/') + 1)
    return id || null
  }
}
